// @ts-strict-ignore
import * as React from 'react';
import {
    IntegrationCapability,
    IntegrationType,
    AllIntegrationTypes,
    IntegrationErrorType,
    SettingType,
    IntegrationInstallationId,
} from '../types';
import { RootState } from '../reducers/reducers';
import { ConnectedProps, connect } from 'react-redux';
import {
    getError,
    getIsLoading,
    getUpdated,
    getAllAppsInOrg,
    getAppGroupsForOrgId,
    allSupportedIntegrationTypesForCapability,
    descriptionForCapability,
    displayNameForCapability,
} from '../helpers';
import { RouteComponentProps } from 'react-router-dom';
import { AppRouteComponentMatchParams, IntegrationRouteProps } from '../typescriptHelpers';
import qs from 'query-string';
import {
    tryCreateIntegrationRequest,
    CreateIntegrationRequest,
    UpdateIntegrationRequest,
    tryUpdateIntegrationRequest,
    CreateIntegrationRequestDataType,
    IntegrationRequest,
    tryDeleteIntegration,
    UpdateIntegrationRequestDataType,
    RefreshIntegrationRequest,
    tryRefreshIntegrationRequest,
    tryUpdateIntegrationAlias,
    UpdateIntegrationAliasRequest,
    TRY_UPDATE_INTEGRATION_ALIAS,
} from '../actions/integrations';
import {
    AuthUpdatable,
    IntegrationDeletable,
    IntegrationRefreshable,
    IntegrationUpdatable,
} from '../actions/updatable';
import {
    createAppSettingsRouteWithSettingType,
    createAppSettingsRoute,
    SettingsScrollTo,
} from '../helpers/routeHelpers';
import {
    authTypeForIntegrationType,
    displayNameForIntegrationType,
    extractIntegrationRequestFromRedirect,
    integrationCapabilitiesForPlatform,
    integrationDeletableForIntegrationType,
    IntegrationCreateOrRefreshRequestOrError,
    integrationUpdatableForIntegrationType,
} from '../helpers/integrationHelpers';
import IntegrationsForCapabilityComponent from './IntegrationsForCapabilityComponent';
import ConnectedIntegrationsComponent from './ConnectedIntegrationsComponent';
import { forIn } from 'lodash';
import IntegrationConfiguration from './IntegrationConfiguration';
import ReleaseBranchPatternsComponent from './ReleaseBranchPatternsComponent';
import ReleaseTagPatternsComponent from './ReleaseTagPatternsComponent';
import FeatureAffiliationsComponent from './FeatureAffiliationsComponent';
import { AppSettingsLocationState } from '../helpers/helperTypes';
import { TRY_REFRESH_INTEGRATIONS } from '../actions/appSettings';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import { allReleasePropertiesFrom } from '../helpers/trackingHelpers';
import * as AnalyticEvents from '../utils/analytics';
import AdditionalBranchesComponent from './AdditionalBranchesComponent';
import { selectOrg, selectIsRunwayOrg, selectApp } from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import Collapse from 'antd/lib/collapse';

const { Panel } = Collapse;

interface State {
    showConfigurationModal: boolean;
    integrationId?: IntegrationInstallationId;
    closable: boolean;
}

class Integrations extends React.Component<Props, State> {
    featureAffiliations: React.RefObject<HTMLDivElement>;
    releaseBranchPatterns: React.RefObject<HTMLDivElement>;
    releaseTagPatterns: React.RefObject<HTMLDivElement>;
    capabilitiesRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {};

    constructor(props) {
        super(props);

        this.updateIntegration = this.updateIntegration.bind(this);
        this.addIntegration = this.addIntegration.bind(this);
        this.deleteIntegration = this.deleteIntegration.bind(this);
        this.updateIntegrationAlias = this.updateIntegrationAlias.bind(this);

        this.featureAffiliations = React.createRef();
        this.releaseBranchPatterns = React.createRef();
        this.releaseTagPatterns = React.createRef();

        this.state = {
            showConfigurationModal: false,
            integrationId: null,
            closable: false,
        };
    }

    componentDidMount() {
        if (
            this.props.integrationCreateOrRefreshRequestOrError &&
            this.props.integrationCreateOrRefreshRequestOrError.error
        ) {
            // Redirect error
            globalMessage.error(this.props.integrationCreateOrRefreshRequestOrError.error);
        }

        if (
            this.props.integrationCreateOrRefreshRequestOrError &&
            this.props.integrationCreateOrRefreshRequestOrError.createRequest &&
            this.props.integrationCreateOrRefreshRequestOrError.authType
        ) {
            const authType = this.props.integrationCreateOrRefreshRequestOrError.authType;
            const createRequest = this.props.integrationCreateOrRefreshRequestOrError.createRequest;
            this.props.addIntegration(authType, createRequest);

            // Clear access code from query params
            this.props.history.replace(this.props.appSettingsIntegrationsRoute);
        } else if (
            this.props.integrationCreateOrRefreshRequestOrError &&
            this.props.integrationCreateOrRefreshRequestOrError.refreshRequest &&
            this.props.integrationCreateOrRefreshRequestOrError.integrationRefreshable
        ) {
            const integrationUpdatable = this.props.integrationCreateOrRefreshRequestOrError.integrationRefreshable;
            const refreshRequest = this.props.integrationCreateOrRefreshRequestOrError.refreshRequest;
            this.props.refreshIntegration(integrationUpdatable, refreshRequest);
            // Clear access code from query params
            this.props.history.replace(this.props.appSettingsIntegrationsRoute);
        }

        if (this.props.scrollTo) {
            switch (this.props.scrollTo) {
                case SettingsScrollTo.FeatureAffiliations:
                    this.featureAffiliations.current?.scrollIntoView(true);
                    break;
                case SettingsScrollTo.ReleaseBranchPatterns:
                    this.releaseBranchPatterns.current?.scrollIntoView(true);
                    break;
                case SettingsScrollTo.ReleaseTagPatterns:
                    this.releaseTagPatterns.current?.scrollIntoView(true);
                    break;
                default: {
                    const integrationCapability = Object.values(IntegrationCapability).find(
                        (cap) => cap === (this.props.scrollTo as string),
                    );
                    if (integrationCapability) {
                        this.capabilitiesRefs[integrationCapability].current?.scrollIntoView({
                            block: 'start',
                            inline: 'start',
                        });
                    }
                    break;
                }
            }
        }

        AnalyticEvents.track(AnalyticEvents.Event.ViewIntegrations, this.props.trackingProperties);
    }

    componentDidUpdate(prevProps: Props) {
        // New integration errors
        forIn(this.props.addIntegrationFailed, (errorMessage, integrationType) => {
            const prevError = prevProps.addIntegrationFailed[integrationType];

            if (!prevError && errorMessage) {
                globalMessage.error(errorMessage);
            }
        });

        // New integration success
        forIn(this.props.addIntegrationSucceeded, (updated, integrationId) => {
            const prevValue = prevProps.addIntegrationSucceeded[integrationId];

            if (!prevValue && updated) {
                const integration = this.props.integrations.find(
                    (integration) => integration.installationId === integrationId,
                );

                if (integration && !integration.integrationError) {
                    const integrationDisplayName = displayNameForIntegrationType(integration.id);
                    globalMessage.success(`${integrationDisplayName} integration added!`);
                } else if (
                    integration &&
                    integration.integrationError?.status === IntegrationErrorType.IncompleteData
                ) {
                    // Incomplete, show configuration modal
                    this.setState({
                        showConfigurationModal: true,
                        integrationId: integration.installationId,
                        closable: false,
                    });
                } else if (integration && integration.integrationError) {
                    globalMessage.error(integration.integrationError.message);
                } else {
                    globalMessage.error('Something went wrong, please try again');
                }
            }
        });

        // Update integration failure

        forIn(this.props.updateIntegrationFailed, (errorMessage, integrationId) => {
            const prevError = prevProps.updateIntegrationFailed[integrationId];

            if (!prevError && errorMessage) {
                globalMessage.error(errorMessage);
            }
        });

        // Update integration success
        forIn(this.props.updateIntegrationSucceeded, (updated, integrationId: IntegrationInstallationId) => {
            const prevValue = prevProps.updateIntegrationSucceeded[integrationId];

            if (!prevValue && updated) {
                const integration = this.props.integrations.find(
                    (integration) => integration.installationId === integrationId,
                );

                if (integration && !integration.integrationError) {
                    const integrationDisplayName = displayNameForIntegrationType(integration.id);
                    globalMessage.success(`${integrationDisplayName} integration updated!`);
                } else if (
                    integration &&
                    integration.integrationError?.status === IntegrationErrorType.IncompleteData
                ) {
                    globalMessage.warning('Please finish configuration of this integration.');
                }

                this.setState({
                    showConfigurationModal: false,
                    integrationId: null,
                });
            }
        });

        // Delete integration failure
        forIn(this.props.deleteIntegrationError, (errorMessage, integrationType) => {
            const prevValue = prevProps.deleteIntegrationError[integrationType];

            if (!prevValue && errorMessage) {
                globalMessage.error(errorMessage);
            }
        });

        // Delete integration success
        forIn(this.props.deleteIntegrationSucceeded, (updated, integrationType) => {
            const prevValue = prevProps.deleteIntegrationSucceeded[integrationType];

            if (!prevValue && updated) {
                const integrationDisplayName = displayNameForIntegrationType(integrationType as IntegrationType);

                globalMessage.success(`${integrationDisplayName} integration removed.`);
            }
        });

        // Refresh integrations

        if (!prevProps.refreshIntegrationsSuccess && this.props.refreshIntegrationsSuccess) {
            globalMessage.success('Integrations refreshed!');
        }

        if (!prevProps.refreshIntegrationsError && this.props.refreshIntegrationsError) {
            globalMessage.error(this.props.refreshIntegrationsError);
        }
    }

    updateIntegration = (
        integrationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => {
        const updateRequest: UpdateIntegrationRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId,
            data,
        };

        const type = integrationUpdatableForIntegrationType(integrationType);

        if (type) {
            this.props.updateIntegration(type, updateRequest);
        } else {
            globalMessage.error('This integration does not support updating.');
        }
    };

    addIntegration = (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => {
        const request: CreateIntegrationRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId: integrationType,
            data,
        };

        const authType = authTypeForIntegrationType(integrationType);

        if (authType) {
            this.props.addIntegration(authType, request);
        } else {
            globalMessage.error('Unrecognized auth type for integration type');
        }
    };

    deleteIntegration = (integrationId: IntegrationInstallationId, integrationType: IntegrationType) => {
        const request: IntegrationRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId,
        };

        const type = integrationDeletableForIntegrationType(integrationType);

        if (type) {
            this.props.deleteIntegration(type, request);
        } else {
            globalMessage.error('This integration does not support deletion.');
        }
    };

    updateIntegrationAlias = (integrationId: IntegrationInstallationId, alias: string) => {
        const request: UpdateIntegrationAliasRequest = {
            orgId: this.props.orgId,
            appId: this.props.appId,
            integrationId,
            alias,
        };
        this.props.updateIntegrationAlias(request);
    };

    render() {
        return (
            <div>
                {this.state.showConfigurationModal && this.state.integrationId && (
                    <IntegrationConfiguration
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        closable={this.state.closable}
                        isLoading={this.props.updateIntegrationIsLoading[this.state.integrationId]}
                        open={this.state.showConfigurationModal}
                        integration={this.props.integrations.find((i) => i.installationId === this.state.integrationId)}
                        updateIntegration={this.updateIntegration}
                        onClose={() => {
                            this.setState({
                                showConfigurationModal: false,
                                integrationId: null,
                            });
                        }}
                    />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 45 }}>
                    {this.props.integrationCapabilities.map((capability) => {
                        const integrationsWithCapability = this.props.integrations.filter((integration) => {
                            return integration.capabilities.includes(capability);
                        });

                        const connectedIntegrationTypes = integrationsWithCapability.map(
                            (integration) => integration.id,
                        );

                        const hasOneConnectedIntegrationForCapability = integrationsWithCapability.length > 0;
                        const allSupportedIntegrations = allSupportedIntegrationTypesForCapability(
                            capability,
                            this.props.isRunwayOrg,
                            this.props.orgId,
                            true,
                        );
                        const description = descriptionForCapability(capability);
                        if (!this.capabilitiesRefs[capability]) {
                            this.capabilitiesRefs[capability] = React.createRef();
                        }
                        return (
                            <div
                                key={capability}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    animationDuration: '0.5s',
                                    animationName: this.props.scrollTo === (capability as string) ? 'flash' : 'none',
                                }}
                                ref={this.capabilitiesRefs[capability]}
                            >
                                <Space direction="vertical" style={{ marginBottom: 42 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
                                        <PlanRestrictedFeatureWrapper
                                            integrationCapability={capability}
                                            shouldFakeDisabledState
                                            requiresTooltip
                                        >
                                            <Title
                                                style={{
                                                    fontSize: '18px',
                                                    flexShrink: 0,
                                                    marginBottom: '0',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {displayNameForCapability(capability)}
                                            </Title>
                                        </PlanRestrictedFeatureWrapper>
                                        <Divider style={{ minWidth: 'auto' }} />
                                    </div>
                                    <Text type="secondary" style={{ maxWidth: '50%', marginBottom: 24, fontSize: 14 }}>
                                        {description}
                                    </Text>
                                </Space>

                                <>
                                    {hasOneConnectedIntegrationForCapability && (
                                        <PlanRestrictedFeatureWrapper integrationCapability={capability}>
                                            {(isDisabled) => (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'stretch',
                                                    }}
                                                >
                                                    <ConnectedIntegrationsComponent
                                                        orgId={this.props.orgId}
                                                        appId={this.props.appId}
                                                        integrations={integrationsWithCapability}
                                                        trackingProperties={this.props.trackingProperties}
                                                        updateIntegration={this.updateIntegration}
                                                        updateIntegrationIsLoading={
                                                            this.props.updateIntegrationIsLoading
                                                        }
                                                        updateIntegrationSuccess={this.props.updateIntegrationSucceeded}
                                                        updateIntegrationAlias={this.updateIntegrationAlias}
                                                        updateIntegrationAliasIsLoading={
                                                            this.props.updateIntegrationAliasIsLoading
                                                        }
                                                        updateIntegrationAliasFailed={
                                                            this.props.updateIntegrationAliasFailed
                                                        }
                                                        updateIntegrationAliasSucceeded={
                                                            this.props.updateIntegrationAliasSucceeded
                                                        }
                                                        showCompleteIntegrationsModal={(
                                                            integrationId: IntegrationInstallationId,
                                                        ) => {
                                                            this.setState({
                                                                showConfigurationModal: true,
                                                                integrationId: integrationId,
                                                                closable: true,
                                                            });
                                                        }}
                                                        deleteIntegration={this.deleteIntegration}
                                                        deleteIntegrationIsLoading={
                                                            this.props.deleteIntegrationIsLoading
                                                        }
                                                        capabilityDisabled={isDisabled}
                                                        capability={capability}
                                                    />
                                                    <Collapse defaultActiveKey={['1']} ghost>
                                                        <Panel header="More providers" key={capability}>
                                                            <IntegrationsForCapabilityComponent
                                                                integrationCapability={capability}
                                                                allSupportedIntegrationsForCapability={
                                                                    allSupportedIntegrations
                                                                }
                                                                connectedIntegrationsForCapability={
                                                                    connectedIntegrationTypes
                                                                }
                                                                isDisabled={isDisabled}
                                                                orgId={this.props.orgId}
                                                                appId={this.props.appId}
                                                                integrationIsLoading={
                                                                    this.props.addIntegrationIsLoading
                                                                }
                                                                addIntegrationSuccess={
                                                                    this.props.addIntegrationSucceeded
                                                                }
                                                                addIntegration={this.addIntegration}
                                                            />
                                                        </Panel>
                                                    </Collapse>
                                                </div>
                                            )}
                                        </PlanRestrictedFeatureWrapper>
                                    )}
                                    {!hasOneConnectedIntegrationForCapability && (
                                        <PlanRestrictedFeatureWrapper integrationCapability={capability}>
                                            {(isDisabled) => (
                                                <IntegrationsForCapabilityComponent
                                                    integrationCapability={capability}
                                                    allSupportedIntegrationsForCapability={allSupportedIntegrations}
                                                    connectedIntegrationsForCapability={connectedIntegrationTypes}
                                                    isDisabled={isDisabled}
                                                    orgId={this.props.orgId}
                                                    appId={this.props.appId}
                                                    integrationIsLoading={this.props.addIntegrationIsLoading}
                                                    addIntegrationSuccess={this.props.addIntegrationSucceeded}
                                                    addIntegration={this.addIntegration}
                                                />
                                            )}
                                        </PlanRestrictedFeatureWrapper>
                                    )}
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'stretch',
                                        }}
                                    >
                                        <div style={{ flexGrow: 1 }}>
                                            {capability === IntegrationCapability.VersionControl && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 24,
                                                        marginTop: 24,
                                                    }}
                                                >
                                                    <ReleaseTagPatternsComponent
                                                        appId={this.props.appId}
                                                        orgId={this.props.orgId}
                                                        refProp={this.releaseTagPatterns}
                                                    />
                                                    <ReleaseBranchPatternsComponent
                                                        appId={this.props.appId}
                                                        orgId={this.props.orgId}
                                                        refProp={this.releaseBranchPatterns}
                                                    />
                                                    <AdditionalBranchesComponent
                                                        appId={this.props.appId}
                                                        orgId={this.props.orgId}
                                                    />
                                                </div>
                                            )}
                                            {capability === IntegrationCapability.FeatureManagement && (
                                                <FeatureAffiliationsComponent
                                                    appId={this.props.appId}
                                                    orgId={this.props.orgId}
                                                    refProp={this.featureAffiliations}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

interface IntegrationProps
    extends IntegrationRouteProps,
        RouteComponentProps<AppRouteComponentMatchParams, { statusCode }, AppSettingsLocationState> {} // TODO: refactor this component into a FC so location can be accessed via hook instead of passed down through props

const mapProps = (state: RootState, ownProps: IntegrationProps) => {
    const { appId, orgId, integrationType, history, location } = ownProps;

    const org = selectOrg(state, orgId);
    const app = selectApp(state, appId);
    const isRunwayOrg = selectIsRunwayOrg(state);
    const appsInOrg = getAllAppsInOrg(orgId, state).map((app) => app.appId);
    const appGroupsInOrg = getAppGroupsForOrgId(orgId, state).map((appGroup) => appGroup.name);

    const trackingProperties = allReleasePropertiesFrom(org, app, null, appsInOrg, appGroupsInOrg);

    const integrations = app.appSettings.integrations;
    const userId = state.user.id;
    const appSettingsIntegrationsRoute = createAppSettingsRouteWithSettingType(
        org.orgId,
        app.appId,
        SettingType.Integrations,
    );
    const appSettingsRoute = createAppSettingsRoute(org.orgId, app.appId);
    const scrollTo = location.state ? location.state.scrollTo : null;
    const integrationCapabilities = integrationCapabilitiesForPlatform(app.appSettings.platform, org.orgId);

    const addIntegrationIsLoading = {};
    const addIntegrationFailed = {};
    const addIntegrationSucceeded = {};

    const orgIdParam = org.orgId;
    const appIdParam = app.appId;

    const refreshIntegrationsIsLoading = getIsLoading(TRY_REFRESH_INTEGRATIONS, state);
    const refreshIntegrationsSuccess = getUpdated(TRY_REFRESH_INTEGRATIONS, state);
    const refreshIntegrationsError = getError(TRY_REFRESH_INTEGRATIONS, state);

    const deleteIntegrationSucceeded = {};

    AllIntegrationTypes.forEach((integrationType) => {
        const authType = authTypeForIntegrationType(integrationType);

        if (authType) {
            const isLoading = getIsLoading(authType, state);
            const failed = getError(authType, state);

            addIntegrationIsLoading[integrationType] = isLoading;
            addIntegrationFailed[integrationType] = failed;
        }

        const deleteType = integrationDeletableForIntegrationType(integrationType);
        if (deleteType) {
            // can't use the installation ID, if this succeeds, that installation is gone
            const updated = getUpdated(deleteType, state);
            deleteIntegrationSucceeded[integrationType] = updated;
        }
    });

    const deleteIntegrationIsLoading = {};
    const deleteIntegrationError = {};

    const updateIntegrationIsLoading = {};
    const updateIntegrationFailed = {};
    const updateIntegrationSucceeded = {};

    const updateIntegrationAliasIsLoading = {};
    const updateIntegrationAliasFailed = {};
    const updateIntegrationAliasSucceeded = {};

    integrations.forEach((integration) => {
        const updateType = integrationUpdatableForIntegrationType(integration.id);

        const updateIsLoading = getIsLoading(updateType, state, integration.installationId);
        const updateFailed = getError(updateType, state, integration.installationId);
        const updateSuccess = getUpdated(updateType, state, integration.installationId);
        updateIntegrationIsLoading[integration.installationId] = updateIsLoading;
        updateIntegrationFailed[integration.installationId] = updateFailed;
        updateIntegrationSucceeded[integration.installationId] = updateSuccess;

        const updateAliasIsLoading = getIsLoading(TRY_UPDATE_INTEGRATION_ALIAS, state, integration.installationId);
        const updateAliasFailed = getError(TRY_UPDATE_INTEGRATION_ALIAS, state, integration.installationId);
        const updateAliasSuccess = getUpdated(TRY_UPDATE_INTEGRATION_ALIAS, state, integration.installationId);
        updateIntegrationAliasIsLoading[integration.installationId] = updateAliasIsLoading;
        updateIntegrationAliasFailed[integration.installationId] = updateAliasFailed;
        updateIntegrationAliasSucceeded[integration.installationId] = updateAliasSuccess;

        const authType = authTypeForIntegrationType(integration.id);
        if (authType) {
            const updated = getUpdated(authType, state, integration.installationId);
            addIntegrationSucceeded[integration.installationId] = updated;
        }

        const deleteType = integrationDeletableForIntegrationType(integration.id);
        if (deleteType) {
            const isLoading = getIsLoading(deleteType, state, integration.installationId);
            const error = getError(deleteType, state, integration.installationId);

            deleteIntegrationIsLoading[integration.installationId] = isLoading;
            deleteIntegrationError[integration.installationId] = error;
        }
    });

    const isRedirect = integrationType !== undefined;
    const queryParameters = qs.parse(location.search);

    const integrationCreateOrRefreshRequestOrError: IntegrationCreateOrRefreshRequestOrError =
        isRedirect ?
            extractIntegrationRequestFromRedirect(
                integrationType,
                queryParameters,
                orgId,
                appId,
                orgIdParam,
                appIdParam,
            )
        :   null;

    return {
        integrations,
        userId,
        orgId,
        appId,
        history,
        appSettingsIntegrationsRoute,
        integrationCreateOrRefreshRequestOrError,
        addIntegrationIsLoading: addIntegrationIsLoading,
        addIntegrationFailed: addIntegrationFailed,
        addIntegrationSucceeded: addIntegrationSucceeded,
        refreshIntegrationsIsLoading,
        refreshIntegrationsSuccess,
        refreshIntegrationsError,
        updateIntegrationFailed,
        updateIntegrationIsLoading,
        updateIntegrationSucceeded,
        updateIntegrationAliasIsLoading,
        updateIntegrationAliasFailed,
        updateIntegrationAliasSucceeded,
        deleteIntegrationError,
        deleteIntegrationIsLoading,
        deleteIntegrationSucceeded,
        scrollTo: scrollTo,
        integrationCapabilities,
        trackingProperties,
        appSettingsRoute,
        isRunwayOrg,
    };
};

const mapDispatch = (dispatch) => {
    return {
        addIntegration: (type: AuthUpdatable, request: CreateIntegrationRequest) => {
            dispatch(
                tryCreateIntegrationRequest(type, {
                    ...request,
                    isQuickStartCI: false,
                }),
            );
        },
        updateIntegration: (type: IntegrationUpdatable, request: UpdateIntegrationRequest) => {
            dispatch(tryUpdateIntegrationRequest(type, request));
        },
        refreshIntegration: (type: IntegrationRefreshable, request: RefreshIntegrationRequest) => {
            dispatch(tryRefreshIntegrationRequest(type, request));
        },
        deleteIntegration: (type: IntegrationDeletable, request: IntegrationRequest) => {
            dispatch(tryDeleteIntegration(type, request));
        },
        updateIntegrationAlias: (request: UpdateIntegrationAliasRequest) => {
            dispatch(tryUpdateIntegrationAlias(request));
        },
    };
};

const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(Integrations);
